/*
 * Sweepstakes landing page (sweepstakes-* sections).
 *
 * - Sale strip ([data-sw-strip]): shows the version whose Start/End days
 *   (YYYY-MM-DD, inclusive) contain today's date in Eastern Time, else the
 *   undated fallback. Liquid already picked one with the shop's time zone;
 *   this corrects it for ET and for cached pages. While the shown version
 *   has savings, html gets .sw-sale-on, which reveals .sw-sale-only items.
 * - Countdown ([data-sw-countdown]): counts down to the next [data-sw-draw]
 *   (date + time in Eastern Time), shows its label, and hides once the last
 *   draw has started. Ticks every 30 seconds.
 * - Draw list ([data-sw-draw-row]): marks each draw Drawn, Up next or
 *   Upcoming from its Eastern Time date and time, every minute.
 * - Sliders ([data-sw-slider]): prev/next arrows scroll the track by a
 *   page of cards, sit at the middle of the card images, and hide at each
 *   end or when nothing overflows. Touch screens swipe. Dots below show
 *   one per screenful of cards and jump there on click.
 * - Enter Free buttons ([data-sw-cta] linking to an anchor) scroll smoothly
 *   to the form and focus its first field once Klaviyo has drawn it.
 * - Analytics: CTA, phone, Official Rules and Instagram clicks and Klaviyo
 *   form submits go to window.dataLayer.
 *
 * Every section includes this file, so it guards against running twice.
 * Theme editor section loads re-run setup; selecting a sale version block
 * previews it.
 */
(() => {
  if (window.swSweepstakes) return;
  window.swSweepstakes = { version: 1 };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function track(event, data = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...data });
  }

  /* Sale strip ----------------------------------------------------------- */

  function easternToday() {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }

  function showVersion(strip, version) {
    strip.querySelectorAll('[data-sw-version]').forEach((item) => {
      item.hidden = item !== version;
    });
    document.documentElement.classList.toggle('sw-sale-on', Boolean(version?.hasAttribute('data-savings')));
  }

  function pickVersion(strip, today) {
    const versions = [...strip.querySelectorAll('[data-sw-version]')];
    let fallback = null;
    for (const version of versions) {
      const start = version.dataset.start || '';
      const end = version.dataset.end || '';
      if (!start && !end) {
        fallback = fallback || version;
        continue;
      }
      if ((!start || today >= start) && (!end || today <= end)) return version;
    }
    return fallback;
  }

  function setupStrip() {
    const strip = document.querySelector('[data-sw-strip]');
    if (!strip) {
      document.documentElement.classList.remove('sw-sale-on');
      return;
    }
    showVersion(strip, pickVersion(strip, easternToday()));
  }

  /* Countdown ------------------------------------------------------------ */

  // The UTC instant of a wall-clock time in New York (EDT is UTC-4, EST UTC-5).
  function easternInstant(date, time) {
    const [y, m, d] = date.split('-').map(Number);
    const [hh, mm] = (time || '12:00').split(':').map(Number);
    if (!y || !m || !d) return NaN;
    const hourIn = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hourCycle: 'h23' });
    for (const offset of [4, 5]) {
      const t = Date.UTC(y, m - 1, d, hh + offset, mm || 0);
      if (Number(hourIn.format(new Date(t))) === hh) return t;
    }
    return Date.UTC(y, m - 1, d, hh + 5, mm || 0);
  }

  let countdownTimer = null;

  function setupCountdown() {
    clearInterval(countdownTimer);
    const bar = document.querySelector('[data-sw-countdown]');
    if (!bar) return;
    const draws = [...bar.querySelectorAll('[data-sw-draw]')]
      .map((el) => ({ at: easternInstant(el.dataset.date || '', el.dataset.time), label: el.dataset.label || '' }))
      .filter((draw) => !Number.isNaN(draw.at))
      .sort((a, b) => a.at - b.at);
    const pad = (n) => String(n).padStart(2, '0');
    const out = {
      label: bar.querySelector('[data-sw-countdown-label]'),
      days: bar.querySelector('[data-sw-countdown-days]'),
      hours: bar.querySelector('[data-sw-countdown-hours]'),
      minutes: bar.querySelector('[data-sw-countdown-minutes]'),
    };

    const tick = () => {
      const now = Date.now();
      const next = draws.find((draw) => draw.at > now);
      if (!next) {
        bar.hidden = true;
        clearInterval(countdownTimer);
        return;
      }
      bar.hidden = false;
      const minutesLeft = Math.ceil((next.at - now) / 60000);
      if (out.label) out.label.textContent = next.label;
      if (out.days) out.days.textContent = pad(Math.floor(minutesLeft / 1440));
      if (out.hours) out.hours.textContent = pad(Math.floor((minutesLeft % 1440) / 60));
      if (out.minutes) out.minutes.textContent = pad(minutesLeft % 60);
    };
    tick();
    countdownTimer = setInterval(tick, 30000);
  }

  /* Draw list ------------------------------------------------------------ */

  let drawRowsTimer = null;

  function setupDrawRows() {
    clearInterval(drawRowsTimer);
    const rows = [...document.querySelectorAll('[data-sw-draw-row]')];
    if (!rows.length) return;
    const items = rows.map((row) => ({ row, at: easternInstant(row.dataset.date || '', row.dataset.time) }));

    const update = () => {
      const now = Date.now();
      const next = items
        .filter((item) => !Number.isNaN(item.at) && item.at > now)
        .sort((a, b) => a.at - b.at)[0];
      for (const item of items) {
        let state = 'upcoming';
        if (!Number.isNaN(item.at) && item.at <= now) state = 'past';
        else if (item === next) state = 'next';
        item.row.dataset.state = state;
        const badge = item.row.querySelector('[data-sw-draw-badge]');
        if (badge && badge.dataset[state]) badge.textContent = badge.dataset[state];
      }
    };
    update();
    drawRowsTimer = setInterval(update, 60000);
  }

  /* Sliders -------------------------------------------------------------- */

  let sliderCleanups = [];

  function setupSliders() {
    sliderCleanups.forEach((cleanup) => cleanup());
    sliderCleanups = [...document.querySelectorAll('[data-sw-slider]')].map((slider) => {
      const track = slider.querySelector('[data-sw-slider-track]');
      const prev = slider.querySelector('[data-sw-slider-prev]');
      const next = slider.querySelector('[data-sw-slider-next]');
      const dots = slider.querySelector('[data-sw-slider-dots]');
      if (!track || !prev || !next) return () => {};
      const behavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

      // Card width + gap, and how many whole cards fit on screen.
      const measure = () => {
        const card = track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const step = card ? card.offsetWidth + gap : track.clientWidth;
        const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / step));
        const max = track.scrollWidth - track.clientWidth;
        // A couple of pixels of slack so sub-pixel widths don't add a phantom page.
        const pages = max > 2 ? Math.ceil((max - 2) / (step * perPage)) + 1 : 1;
        return { step, perPage, max, pages };
      };

      let dotCount = 0;
      const renderDots = (pages) => {
        if (!dots || pages === dotCount) return;
        dotCount = pages;
        dots.replaceChildren(
          ...Array.from({ length: pages }, (_, i) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'sw-slider__dot';
            dot.setAttribute('aria-label', `Show saunas, page ${i + 1} of ${pages}`);
            dot.addEventListener('click', () => {
              const { step, perPage, max } = measure();
              track.scrollTo({ left: Math.min(i * step * perPage, max), behavior: behavior() });
            });
            return dot;
          })
        );
        dots.hidden = pages < 2;
      };

      const update = () => {
        const media = track.querySelector('.sw-product__media');
        if (media) slider.style.setProperty('--sw-slider-media', `${media.offsetHeight}px`);
        const { step, perPage, max, pages } = measure();
        const overflows = max > 2;
        prev.hidden = !overflows;
        next.hidden = !overflows;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max - 2;
        renderDots(pages);
        if (dots) {
          const active = track.scrollLeft >= max - 2 ? pages - 1 : Math.round(track.scrollLeft / (step * perPage));
          [...dots.children].forEach((dot, i) => {
            if (i === active) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        }
      };
      const page = (direction) => {
        const { step, perPage } = measure();
        track.scrollBy({ left: direction * step * perPage, behavior: behavior() });
      };
      const onPrev = () => page(-1);
      const onNext = () => page(1);

      prev.addEventListener('click', onPrev);
      next.addEventListener('click', onNext);
      track.addEventListener('scroll', update, { passive: true });
      const resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(track);
      update();

      return () => {
        prev.removeEventListener('click', onPrev);
        next.removeEventListener('click', onNext);
        track.removeEventListener('scroll', update);
        resizeObserver.disconnect();
      };
    });
  }

  /* Entry buttons -------------------------------------------------------- */

  function focusField(target, triesLeft = 20) {
    const input = target.querySelector('input[type="text"], input[type="email"]');
    if (input) {
      input.focus({ preventScroll: true });
    } else if (triesLeft > 0) {
      setTimeout(() => focusField(target, triesLeft - 1), 150);
    }
  }

  function goTo(link, event) {
    const hash = link.getAttribute('href') || '';
    if (!hash.startsWith('#') || hash.length < 2) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', hash);
    focusField(target);
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a');
    if (!link) return;

    if (link.dataset.swCta) {
      track('sw_cta_click', { sw_location: link.dataset.swCta });
      const plain = event.button === 0 && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
      if (plain) goTo(link, event);
    } else if (link.hasAttribute('data-sw-phone')) {
      track('sw_phone_click');
    } else if (link.hasAttribute('data-sw-rules')) {
      track('sw_rules_click');
    } else if (link.hasAttribute('data-sw-follow')) {
      track('sw_instagram_click');
    }
  });

  // Klaviyo fires "klaviyoForms" on window for its embedded forms.
  window.addEventListener('klaviyoForms', (event) => {
    const detail = event.detail || {};
    if (detail.type !== 'submit') return;
    const form = document.querySelector(`[data-sw-form="${CSS.escape(String(detail.formId || ''))}"]`);
    if (form) track('sw_entry_submit', { sw_form: detail.formId });
  });

  /* Lifecycle ------------------------------------------------------------ */

  function setup() {
    setupStrip();
    setupCountdown();
    setupDrawRows();
    setupSliders();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup);
  else setup();

  document.addEventListener('shopify:section:load', setup);
  // Unload fires before the section leaves the DOM.
  document.addEventListener('shopify:section:unload', () => setTimeout(setup));

  // Theme editor: show a sale version while its block is selected.
  document.addEventListener('shopify:block:select', (event) => {
    const version = event.target.closest('[data-sw-version]');
    const strip = version?.closest('[data-sw-strip]');
    if (strip) showVersion(strip, version);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    if (event.target.closest('[data-sw-version]')) setupStrip();
  });
})();
